import { DateTime } from "luxon"
import { DateTimeUtil } from "@webapp/util/LuxonUtil"
import { assignExisting, isDefined } from "@webapp/util/TypeScriptUtil"
import { Family, ItemType, Person, Sync, WorkspaceTypeInfo, workspaceTypeInfos } from "@webapp/rd/ResearchDataModel"
import { FileUtil } from "@webapp/util/FileUtil"
import { plainToPerson } from "@webapp/rd/PersonStore"
import { plainToFamily } from "@webapp/rd/FamilyStore"

export enum SourceState {
    None = "None",
    Pending = "Pending",
    Ready = "Ready",
    Unrecognized = "Unrecognized",
    Unsupported = "Unsupported",
}

export enum ServerState {
    None = "None",
    Pending = "Pending",
    Ready = "Ready",
}

export class SyncSession {
    id?: string
    agent?: string
    agentVersion?: string
    userId?: string
    createdDate?: DateTime
    sourceFilePath?: string
    sourceType?: string
    sourceState = SourceState.None
    isSourceOutdated = false
    serverState = ServerState.None
    sourceFileModifiedDate?: DateTime
    sourceFileHash?: string
    sourceFileSize?: number
    workspaceId?: string
    lastSyncId?: string
    lastSyncDate?: DateTime
}

export interface SyncSourceInfo {
    name: string
    typeInfo?: WorkspaceTypeInfo
    subtitle?: string
    size?: number
}

export function toSyncSource(source: File | SyncSession | Sync): SyncSourceInfo {
    if (source instanceof File) {
        return {
            name: FileUtil.stripExtension(source.name),
            typeInfo: typeInfoForFileName(source.name),
            subtitle: lastUpdatedText(DateTime.fromMillis(source.lastModified)),
            size: source.size,
        }
    }
    if (source instanceof SyncSession) {
        const fileName = source.sourceFilePath ?? ''
        return {
            name: FileUtil.stripExtension(fileName),
            typeInfo: typeInfoForSourceType(source.sourceType) ?? typeInfoForFileName(fileName),
            subtitle: lastUpdatedText(source.sourceFileModifiedDate),
        }
    }
    const info = source.source
    const fileName = FileUtil.fileName(info?.dataFilePath)
    return {
        name: FileUtil.stripExtension(fileName),
        typeInfo: typeInfoForSourceType(info?.dataFileType) ?? typeInfoForFileName(fileName),
        subtitle: lastUpdatedText(info?.modifiedDate),
    }
}

function typeInfoForSourceType(sourceType: string | undefined) {
    if (!sourceType) {
        return undefined
    }
    const baseType = sourceType.split('.')[0]
    return workspaceTypeInfos.find(t => t.id === baseType)
}

function typeInfoForFileName(fileName: string) {
    const ext = FileUtil.extension(fileName)
    return ext ? workspaceTypeInfos.find(t => t.fileExtensions.includes(ext)) : undefined
}

function lastUpdatedText(date: DateTime | undefined) {
    const rel = date ? DateTimeUtil.toRelativePastDate(date) : undefined
    return rel ? `Last updated ${rel}` : undefined
}

export class SyncScope {
    includeLivingWindowPersonIds?: string[]
    includeContent = false
}

export interface WorkspaceSourcePlaceGeoCodedCounts {
    total: number
    inScope: number
}

export class WorkspaceSourceItemCounts {
    total = 0
    inScope = 0
    added = 0
    modified = 0
    deleted = 0
    dataLoss = 0
    invalid = 0
}

export class WorkspaceSourceDetails {
    itemCounts: Partial<Record<ItemType, WorkspaceSourceItemCounts>> = {}
    placeGeoCodedCounts?: WorkspaceSourcePlaceGeoCodedCounts
    birthCountryCounts: Record<string, number> = {}
}

export type WorkspaceSimilarity = "high" | "medium" | "low"

export class WorkspaceComparison {
    sourcePersonCount = 0
    personMatchScore = 0
    personMatchSampleSize = 0
    personMatchNameOnly = false

    get matchRateText() {
        return (this.personMatchScore * 100).toFixed(1) + "%"
    }

    // Tier the uploaded file's likeness to a prior sync from the sampled match rate, tightened
    // by the person-count difference when the prior count is known.
    similarityTo(lastPersonCount?: number): WorkspaceSimilarity {
        const match = this.personMatchScore >= 0.9 ? "high" : this.personMatchScore >= 0.5 ? "medium" : "low"
        if (!lastPersonCount || !this.sourcePersonCount) {
            return match
        }
        const diffPct = Math.abs((this.sourcePersonCount - lastPersonCount) / lastPersonCount) * 100
        const count = diffPct < 5 ? "high" : diffPct < 10 ? "medium" : "low"
        if (count === "high" && match === "high") {
            return "high"
        }
        if (count === "low" || match === "low") {
            return "low"
        }
        return "medium"
    }
}

export class WorkspaceSourceGraph {
    startPersonId?: string
    persons = new Map<string, Person>()
    families = new Map<string, Family>()
    livingWindowPersonIds = new Set<string>()
    mappedPersonIds = new Set<string>()

    getParentFamilies(personId: string) {
        return this.persons.get(personId)?.childRelationships
            .map(r => this.families.get(r.familyId!))
            .filter(isDefined) ?? []
    }

    getParents(personId: string) {
        const parentIds = this.getParentFamilies(personId).flatMap(f => f.spouseIds)
        return [...new Set(parentIds)]
            .map(id => this.persons.get(id))
            .filter(isDefined)
    }

    getSpouseFamilies(personId: string) {
        return this.persons.get(personId)?.spouseRelationships
            .map(r => this.families.get(r.familyId!))
            .filter(isDefined) ?? []
    }

    getChildren(familyId: string) {
        return [...this.persons.values()].filter(p => p.isChildInFamily(familyId))
    }

    getSpousesOf(personId: string) {
        const spouseIds = this.getSpouseFamilies(personId)
            .map(f => f.getSpouseOf(personId))
            .filter(isDefined)
        return [...new Set(spouseIds)]
            .map(id => this.persons.get(id))
            .filter(isDefined)
    }

    *getAncestors(personId: string, generations: number): Iterable<Person> {
        if (generations > 0) {
            const parents = this.getParents(personId)
            yield* parents
            yield* parents.flatMap(p => [...this.getAncestors(p.id!, generations - 1)])
        }
    }

    *getDescendants(personId: string, generations: number): Iterable<Person> {
        if (generations > 0) {
            const spouseFamilies = this.getSpouseFamilies(personId)
            const spouses = spouseFamilies
                .map(f => f.getSpouseOf(personId)).filter(isDefined)
                .map(id => this.persons.get(id)).filter(isDefined)
            yield* spouses

            const children = spouseFamilies.flatMap(sf => this.getChildren(sf.id!))
            yield* children

            yield* children.flatMap(c => [...this.getDescendants(c.id!, generations - 1)])
        }
    }
}

export class ContentFileRef {
    contentID?: string
    fileName?: string
}

export function plainToSyncSession(p: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
    const s = assignExisting(new SyncSession(), p)
    s.createdDate = DateTimeUtil.fromAPI(p.createdDate)
    s.sourceFileModifiedDate = DateTimeUtil.fromAPI(p.sourceFileModifiedDate)
    s.lastSyncDate = DateTimeUtil.fromAPI(p.lastSyncDate)
    return s
}

export function plainToWorkspaceSourceDetails(p: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
    const d = new WorkspaceSourceDetails()
    if (p?.itemCounts) {
        for (const key of Object.keys(p.itemCounts)) {
            const counts = assignExisting(new WorkspaceSourceItemCounts(), p.itemCounts[key])
            d.itemCounts[key as ItemType] = counts
        }
    }
    if (p?.placeGeoCodedCounts) {
        d.placeGeoCodedCounts = {
            total: p.placeGeoCodedCounts.total ?? 0,
            inScope: p.placeGeoCodedCounts.inScope ?? 0,
        }
    }
    if (p?.birthCountryCounts) {
        d.birthCountryCounts = { ...p.birthCountryCounts }
    }
    return d
}

export function plainToWorkspaceSourceGraph(p: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
    const g = new WorkspaceSourceGraph()
    g.startPersonId = p?.startPersonId
    for (const [id, plainPerson] of Object.entries<any>(p?.persons ?? {})) { // eslint-disable-line @typescript-eslint/no-explicit-any
        g.persons.set(id, plainToPerson(plainPerson))
    }
    for (const [id, plainFamily] of Object.entries<any>(p?.families ?? {})) { // eslint-disable-line @typescript-eslint/no-explicit-any
        g.families.set(id, plainToFamily(plainFamily))
    }
    for (const id of p?.livingWindowPersonIds ?? []) {
        g.livingWindowPersonIds.add(id)
    }
    for (const id of p?.mappedPersonIds ?? []) {
        g.mappedPersonIds.add(id)
    }
    return g
}

export function plainToWorkspaceComparison(p: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
    // The service omits default-valued fields, so coalesce each to the class default rather
    // than letting an absent key clobber it to undefined.
    const c = new WorkspaceComparison()
    c.sourcePersonCount = p.sourcePersonCount ?? c.sourcePersonCount
    c.personMatchScore = p.personMatchScore ?? c.personMatchScore
    c.personMatchSampleSize = p.personMatchSampleSize ?? c.personMatchSampleSize
    c.personMatchNameOnly = p.personMatchNameOnly ?? c.personMatchNameOnly
    return c
}

export function plainToContentFileRef(p: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
    return assignExisting(new ContentFileRef(), p)
}
